import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled, { css, keyframes } from 'styled-components';
import { closeGlobalModal } from './actions';
import { RootState } from '../../redux/store';
import { tr } from '../../translation';
import { ModalProps } from '../../interfaces/connected-components/globalModal';

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

const fadeOut = keyframes`
    from {
        opacity: 1;
    }
    to {
        opacity: 0;
    }
`;

const Container = styled.div<{ isVisible: boolean }>`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  /* Use the Redux prop for animation */
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
  padding: 1rem;
`;

const ContentContainer = styled.div<{ isVisible: boolean }>`
  position: relative;
  background: ${({ theme }) => theme.homeBackground};
  border-radius: 16px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  width: 96%;
  max-width: 500px;
  min-width: 320px;
  display: flex;
  flex-direction: column;

  ${({ isVisible }) =>
    isVisible
      ? css`
          animation: ${fadeIn} 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        `
      : css`
          animation: ${fadeOut} 0.15s cubic-bezier(0.4, 0, 0.2, 1);
        `};

  @media (min-width: 640px) {
    width: 80%;
  }

  @media (min-width: 1024px) {
    width: 35%;
  }
`;

const TopContainer = styled.div`
  padding: 1.5rem;
  flex: 1;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.text.light};
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.4;
  margin: 0 0 0.75rem 0;
`;

const Message = styled.p<{ withPaddingTop: boolean }>`
  color: ${({ theme }) => theme.text.light};
  font-size: 1rem;
  line-height: 1.5;
  margin: ${({ withPaddingTop }) => (withPaddingTop ? '1rem' : '0')} 0 0 0;
  opacity: 0.9;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: ${({ theme }) => theme.homeBackground}dd;
  backdrop-filter: blur(8px);
  border-top: 1px solid ${({ theme }) => `${theme.border}33`};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 0.5rem 0.75rem; /* Reduced padding */
  border-radius: 8px;
  font-size: 0.875rem; /* Slightly smaller font size */
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;

  ${({ variant = 'primary', theme }) =>
    variant === 'primary'
      ? css`
          background: ${theme.primary};
          color: white;
          border: none;

          &:hover {
            filter: brightness(110%);
          }

          &:active {
            transform: scale(0.98);
          }
        `
      : css`
          background: transparent;
          color: ${theme.text.light};
          border: 1px solid ${theme.border};

          &:hover {
            background: ${`${theme.text.border}22`};
          }

          &:active {
            background: ${`${theme.text.border}44`};
          }
        `}
`;

const Overlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  transition: opacity 0.2s ease;
  /* Use the Redux prop for animation */
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
`;

const GlobalModal = ({ globalModal }: ModalProps) => {
  const { message, title, isVisible, type, onConfirm, onReject, customTopComponent, confirmText, rejectText } =
    globalModal || {};

  // Local state used only for mounting/unmounting
  const [isModalMounted, setIsModalMounted] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (isVisible) {
      setIsModalMounted(true);
    } else {
      // Wait for the exit animation to complete
      timeout = setTimeout(() => setIsModalMounted(false), 200);
    }

    document.body.style.overflow = isVisible ? 'hidden' : '';

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isVisible]);

  const handleClose = (status: 'confirm' | 'reject' = 'confirm') => {
    if (status === 'confirm' && onConfirm) {
      onConfirm();
    } else if (status === 'reject' && onReject) {
      onReject();
    }
    closeGlobalModal();
  };

  // Only render if mounted
  if (!isModalMounted) return null;

  return (
    <Container isVisible={isVisible}>
      <Overlay isVisible={isVisible} onClick={() => handleClose('reject')} />
      <ContentContainer isVisible={isVisible}>
        <TopContainer>
          {customTopComponent?.()}
          {title && <Title>{title}</Title>}
          <Message withPaddingTop={!!title || !!customTopComponent}>{message}</Message>
        </TopContainer>
        <ButtonsContainer>
          {type === 'question' && (
            <Button variant="secondary" onClick={() => handleClose('reject')}>
              {rejectText || tr('app.cancel')}
            </Button>
          )}
          <Button variant="primary" onClick={() => handleClose()}>
            {confirmText || (type === 'question' ? tr('app.yes') : tr('app.ok'))}
          </Button>
        </ButtonsContainer>
      </ContentContainer>
    </Container>
  );
};

const mapStateToProps = (state: RootState) => ({
  globalModal: state.globalModal,
});

export default connect(mapStateToProps)(GlobalModal);
